import { ApiResult } from '@src/api/apiClient/apiResult';
import { axiosClient } from '@src/api/apiClient/axiosClient';
import { Pagination } from '@src/api/types/pagination';
import { IndexRequest } from '@src/api/types/shared/request';

export type CompanyUser = {
  id: number;
  email: string;
  created_on: string;
  family_name: string;
  first_name: string;
  birthday: string;
  company_role_name: string;
  status: boolean;
  authenticated: boolean;
};

export type CompanyUserIndexResponse = {
  data: CompanyUser[];
  pagination: Pagination;
};

type CompanyUserCreateRequest = {
  company_user: {
    family_name: string;
    first_name: string;
    family_name_kana: string;
    first_name_kana: string;
    email: string;
    birthday?: string;
    company_role_id: string;
  };
};

export type CompanyUserCreateResponse = unknown;

export type CompanyUserCreateErrorResponse = {
  company_user: {
    family_name: string[];
    first_name: string[];
    family_name_kana: string[];
    first_name_kana: string[];
    email: string[];
    birthday: string[];
    company_role_id: string[];
  };
};

export type CompanyUserShowResponse = {
  id: number;
  company_role_id: string;
  company_role_name: string;
  family_name: string;
  first_name: string;
  family_name_kana: string;
  first_name_kana: string;
  email: string;
  birthday: string;
  expires_at: string;
  enabled: boolean;
  can_resend_invitation: boolean;
};

export type TemporaryCompanyUserShowResponse = {
  family_name: string;
  first_name: string;
  family_name_kana: string;
  first_name_kana: string;
  email: string;
  birthday: string;
};

export type CompanyUserUpdateRequest = {
  company_user: {
    family_name: string;
    first_name: string;
    family_name_kana: string;
    first_name_kana: string;
    birthday: string;
    company_role_id: string;
    enabled?: boolean;
  };
};

export type CompanyUserUpdateResponse = unknown;

export type CompanyUserUpdateErrorResponse = {
  company_user: {
    family_name: string[];
    first_name: string[];
    family_name_kana: string[];
    first_name_kana: string[];
    birthday: string[];
    company_role_id: string[];
    enabled: string[];
  };
};

export type SuggestEmailRequest = {
  family_name: string;
  first_name: string;
  suggested_email: string;
};

export type SuggestEmailResponse = unknown;

export type SuggestEmailErrorResponse = {
  suggested_email: string[];
};

export const companyUserApiClient = {
  index: async (params: IndexRequest = {}): Promise<ApiResult<CompanyUserIndexResponse>> => {
    return await axiosClient.get('/company/company-users', { params });
  },
  create: async (
    data: CompanyUserCreateRequest,
  ): Promise<ApiResult<CompanyUserCreateResponse, CompanyUserCreateErrorResponse>> => {
    return await axiosClient.post('/company/company-users', data);
  },
  show: async (id: number): Promise<ApiResult<CompanyUserShowResponse>> => {
    return await axiosClient.get(`/company/company-users/${id}`);
  },
  update: async (
    id: number,
    data: CompanyUserUpdateRequest,
  ): Promise<ApiResult<CompanyUserUpdateResponse, CompanyUserUpdateErrorResponse>> => {
    return await axiosClient.put(`/company/company-users/${id}`, data);
  },
  delete: async (id: number): Promise<ApiResult> => {
    return await axiosClient.delete(`/company/company-users/${id}`);
  },
  sendSuggestion: async (
    id: number,
    data: SuggestEmailRequest,
  ): Promise<ApiResult<SuggestEmailResponse, SuggestEmailErrorResponse>> => {
    return await axiosClient.put(`/company/company-users/${id}/email/suggestion`, data);
  },
  showTemporary: async (id: number): Promise<ApiResult<TemporaryCompanyUserShowResponse>> => {
    return await axiosClient.get(`/company/company-users/temporaries/${id}`);
  },
};
